<script setup>
import { ref, computed, nextTick, onMounted, onUnmounted } from 'vue';
import AppIcon from "./AppIcon.vue";

const props = defineProps({
  modelValue: { required: true },
  options: { type: Array, required: true }, // [{ value, label }]
  size: { type: String, default: 'medium' },
  color: { type: String, default: 'green' },
  placeholder: { type: String, default: 'Select...' },
});

const emit = defineEmits(['update:modelValue', 'change']);

const open = ref(false);
const filter = ref('');
const inputRef = ref(null);
const rootRef = ref(null);

const selectedLabel = computed(() => {
  const opt = props.options.find(o => o.value === props.modelValue);
  return opt ? opt.label : props.placeholder;
});

const filtered = computed(() => {
  const q = filter.value.toLowerCase();
  if (!q) return props.options;
  return props.options.filter(o => o.label.toLowerCase().includes(q));
});

async function toggle() {
  open.value = !open.value;
  if (open.value) {
    filter.value = '';
    await nextTick();
    inputRef.value?.focus();
  }
}

function select(value) {
  emit('update:modelValue', value);
  emit('change', value);
  open.value = false;
  filter.value = '';
}

function onKeydown(e) {
  if (e.key === 'Escape') {
    open.value = false;
    filter.value = '';
  } else if (e.key === 'Enter' && filtered.value.length > 0) {
    select(filtered.value[0].value);
  }
}

function onClickOutside(e) {
  if (rootRef.value && !rootRef.value.contains(e.target)) {
    open.value = false;
    filter.value = '';
  }
}

onMounted(() => document.addEventListener('mousedown', onClickOutside));
onUnmounted(() => document.removeEventListener('mousedown', onClickOutside));
</script>

<template>
  <div class="ui-filter-select" :class="`ui-filter-select-${size}`" ref="rootRef">
    <button
      type="button"
      @click="toggle"
      :class="['ui-select', `ui-button-${color}`, `ui-select-${size}`, 'ui-filter-trigger']"
    >
      <span class="ui-filter-trigger-label">{{ selectedLabel }}</span>
      <AppIcon name="chevron-down" class="ui-filter-chevron" :class="{ 'ui-filter-chevron-open': open }" />
    </button>
    <div v-if="open" class="ui-filter-dropdown">
      <div class="ui-filter-search">
        <input
          ref="inputRef"
          v-model="filter"
          @keydown="onKeydown"
          placeholder="Filter..."
          class="ui-filter-input"
          type="text"
        />
      </div>
      <div class="ui-filter-options">
        <div
          v-for="opt in filtered"
          :key="opt.value"
          class="ui-filter-option"
          :class="{ 'ui-filter-option-selected': opt.value === modelValue }"
          @mousedown.prevent="select(opt.value)"
        >
          {{ opt.label }}
        </div>
        <div v-if="filtered.length === 0" class="ui-filter-empty">No results</div>
      </div>
    </div>
  </div>
</template>
